(function () {
  'use strict';

  const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const TIMEOUT_MS = 15000;
  let mapsPromise;

  function parseLocation(suburb, postcode) {
    suburb = String(suburb || '').trim();
    postcode = String(postcode || '').trim();
    const combined = suburb.match(/^(.+?),\s*(\d{4})$/);
    if (combined && !postcode) {
      suburb = combined[1].trim();
      postcode = combined[2];
    }
    if (suburb.length < 2) return { error: 'suburb_required' };
    if (!/^\d{4}$/.test(postcode)) return { error: 'postcode_required' };
    return { suburb, postcode };
  }

  function formatTime(value) {
    const match = String(value).match(/^(\d{2}):(\d{2})(?::\d{2})?$/);
    if (!match || Number(match[1]) > 23 || Number(match[2]) > 59) throw new Error('Invalid delivery time');
    const hour = Number(match[1]);
    return `${hour % 12 || 12}:${match[2]} ${hour >= 12 ? 'PM' : 'AM'}`;
  }

  function normalizeResult(options, zones, night, pricing) {
    if (!Array.isArray(options) || !zones || typeof zones !== 'object' || !night || typeof night.night_time !== 'string') {
      throw new Error('Invalid delivery response');
    }
    const status = night.night_time.trim().toLowerCase();
    if (!['enable', 'disable'].includes(status)) throw new Error('Invalid delivery preference');
    const rates = pricing && pricing.rates;
    if (pricing !== null && (!rates || !['shippingPrice', 'minimunOrderVal', 'freeshippingover'].every((key) =>
      rates[key] !== null && rates[key] !== '' && Number.isFinite(Number(rates[key])) && Number(rates[key]) >= 0
    ) || Number(rates.minimunOrderVal) <= 0 || Number(rates.freeshippingover) <= 0)) {
      throw new Error('Delivery pricing unavailable');
    }
    if (!zones.time_windows || typeof zones.time_windows !== 'object') throw new Error('Delivery zone unavailable');
    if (!options.length) return { available: false };

    const next = options[status === 'enable' ? 1 : 0];
    if (!next) return { available: false };
    if (!/^\d{4}-\d{2}-\d{2}$/.test(next.date) || !DAYS.includes(next.day)) throw new Error('Invalid delivery date');
    const date = new Date(`${next.date}T12:00:00`);
    if (Number.isNaN(date.getTime())) throw new Error('Invalid delivery date');
    const nextTime = `${formatTime(next.time_start)} - ${formatTime(next.time_end)}`;
    const schedule = {};
    Object.values(zones.time_windows).forEach((window) => {
      if (!window || !DAYS.includes(window.day)) throw new Error('Invalid delivery schedule');
      formatTime(window.time_start);
      formatTime(window.time_end);
      let [startHour, startMin] = window.time_start.split(':').map(Number);
      const [endHour, endMin] = window.time_end.split(':').map(Number);
      if (status === 'enable') {
        if ((startHour < 7 && endHour <= 7) || (startHour < 7 && endHour > 7 && endMin === 0)) return;
        if (startHour < 7) { startHour = 7; startMin = 0; }
      }
      const start = `${String(startHour).padStart(2, '0')}:${String(startMin).padStart(2, '0')}`;
      (schedule[window.day] || (schedule[window.day] = [])).push(`${formatTime(start)} - ${formatTime(window.time_end)}`);
    });
    if (!Object.keys(schedule).length) return { available: false };
    return {
      available: true,
      next: { day: next.day, date: next.date, time: nextTime },
      schedule,
      rates: rates ? { shipping: Number(rates.shippingPrice), minimum: Number(rates.minimunOrderVal), free: Number(rates.freeshippingover) } : null
    };
  }

  async function getJSON(url, options, signal, fetcher) {
    // Retry only transient read failures; never retry invalid locations or access errors.
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const response = await fetcher(url.toString(), Object.assign({}, options, { signal }));
        if (!response.ok) {
          const error = new Error(`Delivery service HTTP ${response.status}`);
          error.status = response.status;
          if (attempt === 0 && [408, 429, 502, 503, 504].includes(response.status)) continue;
          throw error;
        }
        return await response.json();
      } catch (error) {
        if (attempt === 0 && !signal.aborted && error.name === 'TypeError') continue;
        throw error;
      }
    }
  }

  async function lookup(location, config, signal, fetcher = fetch) {
    const optionsURL = new URL('https://api.becoolcouriers.com.au/deliveries/optionsadv');
    optionsURL.search = new URLSearchParams({ use_linehaul: 'true', use_pickpack: 'true', postcode: location.postcode, max_options: '2', suburb: location.suburb });
    const zonesURL = new URL('https://api.becoolcouriers.com.au/zones');
    zonesURL.search = new URLSearchParams({ postcode: location.postcode, timewindow: 'true' });
    const pricingURL = new URL('https://custom-shipping-rates.fly.dev/get_pricing.php');
    pricingURL.search = new URLSearchParams({ postcode: location.postcode, timewindow: 'true' });
    const courierOptions = { credentials: 'include', headers: { 'X-Api-Key': config.courierApiKey, 'Content-Type': 'application/json' } };
    const responses = await Promise.allSettled([
      getJSON(optionsURL, courierOptions, signal, fetcher),
      getJSON(zonesURL, courierOptions, signal, fetcher),
      getJSON('https://www.cinemano.com.au/time_slot_api/check_night_time_on-off.php', {}, signal, fetcher),
      getJSON(pricingURL, { headers: { 'Content-Type': 'application/json' } }, signal, fetcher)
    ]);
    const value = (index) => responses[index].status === 'fulfilled' ? responses[index].value : null;
    const zones = value(1);
    if (!zones || !zones.time_windows || typeof zones.time_windows !== 'object') {
      if (responses[1].reason && responses[1].reason.status === 404) return { available: false };
      throw new Error('Delivery zone unavailable');
    }
    const options = value(0);
    if (Array.isArray(options) && !options.length) return { available: false };
    if (!Array.isArray(options)) {
      const error = new Error('Delivery options unavailable');
      error.locationUnconfirmed = responses[0].reason && responses[0].reason.status === 400;
      throw error;
    }
    const night = value(2);
    const preferenceKnown = night && typeof night.night_time === 'string' && ['enable', 'disable'].includes(night.night_time.trim().toLowerCase());
    // When the preference service is unavailable, show verified daytime windows only,
    // explicitly labelled as such, rather than assuming overnight delivery is enabled.
    const daytimeOnly = !preferenceKnown || night.night_time.trim().toLowerCase() === 'enable';
    const daytime = (window) => window && /^\d{2}:\d{2}(?::\d{2})?$/.test(window.time_start) && Number(window.time_start.slice(0, 2)) >= 7;
    const eligible = options.filter((option) => !daytimeOnly || daytime(option));
    const cleanZones = Object.assign({}, zones, { time_windows: Object.values(zones.time_windows).filter((window) => window && window.in_use !== false && (!daytimeOnly || daytime(window))) });
    if (!eligible.length || !cleanZones.time_windows.length) throw new Error('No confirmed delivery window');
    const pricing = value(3);
    let rates = null;
    try {
      // Reuse the strict pricing contract; a pricing outage must not invent rates.
      rates = normalizeResult(eligible, cleanZones, { night_time: 'disable' }, pricing).rates;
    } catch (_) { /* Date and zone validation still run independently below. */ }
    const result = normalizeResult(eligible, cleanZones, { night_time: 'disable' }, null);
    result.rates = rates;
    result.daytimeOnly = !preferenceKnown;
    result.partial = !preferenceKnown || !rates;
    return result;
  }

  function loadPlaces(key) {
    if (window.google && google.maps && google.maps.places && google.maps.places.Autocomplete) return Promise.resolve();
    if (mapsPromise) return mapsPromise;
    mapsPromise = new Promise((resolve, reject) => {
      if (!key) { reject(new Error('Suburb suggestions unavailable')); return; }
      const script = document.createElement('script');
      const timer = setTimeout(() => reject(new Error('Suburb suggestions timed out')), TIMEOUT_MS);
      window.providoorDeliveryPlacesReady = () => {
        clearTimeout(timer);
        if (window.google && google.maps && google.maps.places && google.maps.places.Autocomplete) resolve();
        else reject(new Error('Suburb suggestions unavailable'));
      };
      script.src = `https://maps.googleapis.com/maps/api/js?${new URLSearchParams({ key, libraries: 'places', loading: 'async', callback: 'providoorDeliveryPlacesReady' })}`;
      script.async = true;
      script.onerror = () => { clearTimeout(timer); reject(new Error('Suburb suggestions unavailable')); };
      document.head.appendChild(script);
    });
    return mapsPromise;
  }

  class ProvidoorDeliveryChecker extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      const connectionId = this.connectionId = (this.connectionId || 0) + 1;
      this.config = JSON.parse(this.querySelector('[data-delivery-config]').textContent);
      this.form = this.querySelector('[data-delivery-form]');
      this.suburb = this.querySelector('[data-delivery-suburb]');
      this.postcode = this.querySelector('[data-delivery-postcode]');
      this.button = this.querySelector('[data-delivery-submit]');
      this.guidance = this.querySelector('[data-delivery-guidance]');
      this.status = this.querySelector('[data-delivery-status]');
      this.results = this.querySelector('[data-delivery-results]');
      this.requestId = 0;
      this.fieldVersion = 0;
      this.manualLocation = { suburb: this.suburb.value, postcode: this.postcode.value };
      this.onSubmit = (event) => {
        event.preventDefault();
        if (document.activeElement !== this.suburb || !this.selectionIntent) this.submit();
      };
      this.onInput = (event) => {
        this.fieldVersion++;
        this.selectionIntent = null;
        if (event && event.target === this.postcode) {
          this.manualLocation.postcode = this.postcode.value;
          this.suburb.value = this.manualLocation.suburb;
        } else if (event && event.target === this.suburb) {
          this.manualLocation.suburb = this.suburb.value;
        } else {
          this.manualLocation = { suburb: this.suburb.value, postcode: this.postcode.value };
        }
        this.requestId++;
        if (this.controller) this.controller.abort();
        clearTimeout(this.timer);
        this.setPending(false);
        this.results.replaceChildren();
        this.setStatus('');
      };
      this.recordSelection = (pointer = false) => {
        if (document.activeElement !== this.suburb) return;
        this.selectionIntent = { version: this.fieldVersion, query: this.manualLocation.suburb, time: Date.now(), pointer };
      };
      this.onSuburbKeydown = (event) => {
        if (event.key === 'Enter') {
          const selected = this.autocomplete && document.querySelector('.pac-container .pac-item-selected');
          if (!selected || !selected.getClientRects().length) {
            event.preventDefault();
            event.stopPropagation();
            this.selectionIntent = null;
            this.submit();
            return;
          }
        }
        if (this.autocomplete && ['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) this.recordSelection();
      };
      this.onSuggestionPointer = (event) => {
        if (event.target.closest && event.target.closest('.pac-item')) this.recordSelection(true);
        else this.selectionIntent = null;
      };
      this.onPostcodeKeydown = (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        event.stopPropagation();
        this.selectionIntent = null;
        this.suburb.value = this.manualLocation.suburb;
        this.submit();
      };
      this.form.addEventListener('submit', this.onSubmit);
      this.suburb.addEventListener('input', this.onInput);
      this.postcode.addEventListener('input', this.onInput);
      this.suburb.addEventListener('keydown', this.onSuburbKeydown, true);
      this.postcode.addEventListener('keydown', this.onPostcodeKeydown, true);
      document.addEventListener('pointerdown', this.onSuggestionPointer, true);
      loadPlaces(this.config.googleApiKey).then(() => {
        if (!this.isConnected || connectionId !== this.connectionId) return;
        this.autocomplete = new google.maps.places.Autocomplete(this.suburb, {
          types: ['(regions)'], componentRestrictions: { country: 'au' }, fields: ['address_components']
        });
        this.placeListener = this.autocomplete.addListener('place_changed', () => {
          const place = this.autocomplete.getPlace();
          const components = Array.isArray(place.address_components) ? place.address_components : [];
          const locality = components.find((component) => Array.isArray(component.types) && component.types.includes('locality'));
          const postal = components.find((component) => Array.isArray(component.types) && component.types.includes('postal_code'));
          const intent = this.selectionIntent;
          this.selectionIntent = null;
          const normalize = (value) => String(value || '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
          const query = normalize(intent && intent.query);
          const matchesQuery = [locality, postal].some((component) => {
            const name = normalize(component && component.long_name);
            return query && name && name.startsWith(query);
          });
          const currentSelection = intent && intent.version === this.fieldVersion && Date.now() - intent.time < TIMEOUT_MS &&
            (document.activeElement === this.suburb || intent.pointer) && matchesQuery && !this.button.disabled;
          if (!currentSelection) {
            this.suburb.value = this.manualLocation.suburb;
            this.postcode.value = this.manualLocation.postcode;
            return;
          }
          this.suburb.value = locality ? locality.long_name : '';
          this.postcode.value = postal ? postal.long_name : '';
          this.onInput();
          if (locality && postal) {
            this.guidance.textContent = '';
            this.button.focus();
          } else {
            this.guidance.textContent = this.message(locality ? 'postcode_needed' : 'suburb_needed');
            (locality ? this.postcode : this.suburb).focus();
          }
        });
      }).catch(() => {
        if (this.isConnected && connectionId === this.connectionId) this.guidance.textContent = this.message('suggestions_unavailable');
      });
    }

    disconnectedCallback() {
      this.connectionId++;
      this.requestId++;
      if (this.controller) this.controller.abort();
      clearTimeout(this.timer);
      if (this.placeListener) this.placeListener.remove();
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      if (this.suburb) this.suburb.removeEventListener('input', this.onInput);
      if (this.postcode) this.postcode.removeEventListener('input', this.onInput);
      if (this.suburb) this.suburb.removeEventListener('keydown', this.onSuburbKeydown, true);
      if (this.postcode) this.postcode.removeEventListener('keydown', this.onPostcodeKeydown, true);
      document.removeEventListener('pointerdown', this.onSuggestionPointer, true);
      this.initialized = false;
    }

    message(key) { return this.config.messages[key] || ''; }
    setStatus(text, error = false) {
      this.status.textContent = text;
      this.status.dataset.error = String(error);
    }
    setPending(pending) {
      this.button.disabled = pending;
      this.button.textContent = this.message(pending ? 'checking' : 'check');
      this.results.setAttribute('aria-busy', String(pending));
    }

    async submit() {
      this.selectionIntent = null;
      this.suburb.value = this.manualLocation.suburb;
      this.postcode.value = this.manualLocation.postcode;
      const location = parseLocation(this.suburb.value, this.postcode.value);
      if (location.error) {
        this.setStatus(this.message(location.error), true);
        (location.error === 'suburb_required' ? this.suburb : this.postcode).focus();
        return;
      }
      const lookupKey = `${location.suburb.toLowerCase()}|${location.postcode}`;
      this.guidance.textContent = '';
      if (this.button.disabled && this.lookupKey === lookupKey) return;
      this.lookupKey = lookupKey;
      const id = ++this.requestId;
      if (this.controller) this.controller.abort();
      clearTimeout(this.timer);
      const controller = new AbortController();
      this.controller = controller;
      this.results.replaceChildren();
      this.setPending(true);
      this.setStatus(this.message('checking'));
      this.timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
      try {
        const result = await lookup(location, this.config, controller.signal);
        if (id !== this.requestId || !this.isConnected) return;
        // A timed-out optional service may still leave verified results; edited inputs use requestId cancellation.
        if (!result.available) {
          this.setStatus(this.message('no_options'));
          return;
        }
        this.render(result, location);
        this.setStatus(result.partial ? 'Available information is shown below. Confirm final delivery dates and charges at checkout.' : this.message('ready'));
      } catch (error) {
        if (id === this.requestId && this.isConnected) this.setStatus(error.locationUnconfirmed ? 'We could not match this suburb and postcode. Enter the exact suburb name (for example, Brisbane City rather than Brisbane), or select a suburb suggestion, then try again.' : this.message('lookup_failed'), true);
      } finally {
        if (id === this.requestId) { clearTimeout(this.timer); this.setPending(false); }
      }
    }

    render(result, location) {
      const node = (tag, text, className) => {
        const element = document.createElement(tag);
        if (text) element.textContent = text;
        if (className) element.className = className;
        return element;
      };
      const section = (title) => {
        const wrapper = node('div');
        wrapper.appendChild(node('h3', title));
        const panel = node('div', '', 'delivery-checker-panel');
        wrapper.appendChild(panel);
        this.results.appendChild(wrapper);
        return panel;
      };
      this.results.appendChild(node('h3', `${this.message('delivery_for')} ${location.suburb} (${location.postcode})`));
      const date = new Intl.DateTimeFormat('en-AU', { year: 'numeric', month: 'short', day: '2-digit' }).format(new Date(`${result.next.date}T12:00:00`));
      section(result.daytimeOnly ? 'Next confirmed daytime courier option' : this.message('next_delivery')).appendChild(node('p', `${this.message(result.next.day.toLowerCase())} ${date}: ${result.next.time}`));
      if (result.daytimeOnly) this.results.appendChild(node('p', 'Overnight delivery preferences could not be checked. Only confirmed daytime courier windows are shown; checkout confirms the options for your order.'));
      const schedule = section(result.daytimeOnly ? 'Daytime courier schedule' : this.message('schedule'));
      DAYS.forEach((day) => {
        if (result.schedule[day]) schedule.appendChild(node('p', `${this.message(day.toLowerCase())}: ${result.schedule[day].join(', ')}`));
      });
      const pricing = section(this.message('pricing'));
      if (!result.rates) pricing.appendChild(node('p', 'Delivery charges and order thresholds could not be confirmed right now. Check the final charges at checkout.'));
      if (result.rates) {
      const money = (value) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: this.config.currency || 'AUD' }).format(value);
      [['minimum_order', result.rates.minimum], ['shipping_fee', result.rates.shipping], ['free_shipping', result.rates.free]].forEach(([label, value]) => {
        pricing.appendChild(node('p', `${this.message(label)}: ${money(value)}`));
      });
      pricing.appendChild(node('p', this.message('pricing_note')));
      }
      this.results.appendChild(node('p', this.message('checkout_note')));
      const getting = this.querySelector('[data-delivery-getting]');
      if (getting) this.results.appendChild(getting.content.cloneNode(true));
    }
  }

  if (!customElements.get('providoor-delivery-checker')) customElements.define('providoor-delivery-checker', ProvidoorDeliveryChecker);
})();

